import { Button } from '@heidi/design-system/button';
import {
  TypographyH3,
  TypographyH5,
  TypographyP,
  TypographyP2,
} from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { getFollowUpQuestions } from '@/pages/api/scribeAPIV2';
import { isChatFollowUpQuestionsApiAvailable } from '@/utils/backendSurfaces';
import { useTranslations } from '@/utils/translations';
import type { FollowUpQuestion } from '@heidi/evidence/contracts';
import { useQuery } from '@tanstack/react-query';
import { CornerDownRight } from 'lucide-react';
import { memo, useState, type JSX } from 'react';
import { track } from '@/lib/analytics/track';

// Query key for follow-up questions - unique per chat + response content
export const FOLLOW_UP_QUESTIONS_KEY = 'follow-up-questions';

interface FollowUpChipsProps {
  /** Chat ID for the current conversation */
  chatId: string | undefined;
  /** Backend message ID (msg_*) of the assistant response the chips render under */
  messageId?: string;
  /** Session ID if within a scribe session */
  sessionId?: string;
  /** The user's last query */
  userQuery: string;
  /** The assistant's response */
  assistantResponse: string;
  /** Tool calls made during the response (e.g., 'web_search') */
  toolCalls?: string[];
  /** User's medical specialty */
  specialty?: string;
  /** Whether the response is still streaming */
  isStreaming: boolean;
  /** Callback when a follow-up question is clicked */
  onSelectQuestion: (question: string) => void;
  /** Additional CSS class */
  className?: string;
  /** Whether this is the last text response in the chat (only show follow-ups for last) */
  isLastResponse?: boolean;
  /** Whether to use compact styling (smaller text) for in-session views */
  compact?: boolean;
}

/**
 * FollowUpChips - Display AI-generated follow-up question suggestions as clickable chips.
 *
 * Shows after a chat response completes, allowing users to quickly ask related questions.
 * Uses React Query for:
 * - Automatic deduplication (same chat won't fetch twice)
 * - Caching (switching back to a chat uses cached data)
 * - Built-in loading states
 */
export const FollowUpChips = memo(
  ({
    chatId,
    messageId,
    sessionId,
    userQuery,
    assistantResponse,
    toolCalls,
    specialty,
    isStreaming,
    onSelectQuestion,
    className,
    isLastResponse = true,
    compact = false,
  }: FollowUpChipsProps): JSX.Element | null => {
    const { t, locale } = useTranslations();
    const [isHidden, setIsHidden] = useState(false);

    // Determine if we should fetch
    // - Not streaming
    // - This is the last response
    // - We have required data
    // - Response is substantial (>50 chars)
    // - The server serves the route at all: `POST /chat/follow-up-questions`
    //   stays in ml-scribe's cloud-only chat router, so on an on-prem build the
    //   request could only 404 after every answer. Disabled, the query is idle
    //   with no questions and the component renders nothing below.
    const shouldFetch =
      isChatFollowUpQuestionsApiAvailable &&
      !isStreaming &&
      isLastResponse &&
      !!chatId &&
      !!userQuery &&
      !!assistantResponse &&
      assistantResponse.length > 50;

    // React Query handles caching, deduplication, and loading state
    // Query key includes chatId + userQuery hash for uniqueness per response
    const { data: questions = [], isLoading } = useQuery({
      queryKey: [
        FOLLOW_UP_QUESTIONS_KEY,
        chatId,
        userQuery,
        // Use first 100 chars of response as part of key to differentiate responses
        assistantResponse.slice(0, 100),
      ],
      queryFn: async () => {
        const response = await getFollowUpQuestions({
          chatId: chatId!,
          userQuery,
          assistantResponse: assistantResponse.slice(0, 3000), // Truncate for API
          toolCalls,
          specialty,
          displayLanguage: locale,
        });
        return response.data.questions;
      },
      enabled: shouldFetch,
      staleTime: Infinity, // Never refetch - questions are tied to specific response
      gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
      retry: false, // Don't retry on failure - just hide chips
    });

    // Don't render if still streaming
    if (isStreaming) {
      return null;
    }

    // Don't render if this is not the last response
    if (!isLastResponse) {
      return null;
    }

    // Don't render if hidden (after selection) or no questions
    if (isHidden || (!isLoading && questions.length === 0)) {
      return null;
    }

    const handleClick = (question: string, index: number) => {
      // Track follow-up question clicked
      track({ options: { amplitude: false },
        event_name: TrackingEventName.FollowUpQuestionClicked,
        props: {
          question,
          chat_id: chatId,
          message_id: messageId,
          session_id: sessionId,
          question_index: index,
          total_questions: questions.length,
        },
      });

      onSelectQuestion(question);
      setIsHidden(true); // Hide chips after selection
    };

    return (
      <div
        className={cn('mt-6 sm:mt-12 flex flex-col gap-2', className)}
        data-testid="follow-up-chips"
      >
        {/* Header */}
        {compact ? (
          <TypographyH5 className="font-exposure text-text-secondary">
            {t('chat.askFollowUp')}
          </TypographyH5>
        ) : (
          <TypographyH3 className="font-exposure text-lg sm:text-2xl text-text-secondary">
            {t('chat.askFollowUp')}
          </TypographyH3>
        )}

        {/* Questions List */}
        <div className="flex flex-col gap-1">
          {isLoading ? (
            // Loading skeleton rows
            <>
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-2 py-2">
                  <div className="size-4 animate-pulse rounded bg-background-secondary" />
                  <div className="h-5 flex-1 animate-pulse rounded bg-background-secondary" />
                </div>
              ))}
            </>
          ) : (
            questions.map((q: FollowUpQuestion, index: number) => {
              const TextComponent = compact ? TypographyP2 : TypographyP;
              return (
                <Button
                  key={`${q.question}-${index}`}
                  variant="ghost"
                  size="md"
                  className="h-auto items-start justify-start gap-2 whitespace-normal py-2 text-text-secondary hover:text-text-primary"
                  onClick={() => handleClick(q.question, index)}
                >
                  <span
                    className={cn(
                      'flex shrink-0 items-center',
                      compact ? 'h-5' : 'h-6'
                    )}
                  >
                    <CornerDownRight className="size-4 rtl:-scale-x-100" />
                  </span>
                  <TextComponent className="min-w-0 break-words text-left">{q.question}</TextComponent>
                </Button>
              );
            })
          )}
        </div>
      </div>
    );
  }
);

FollowUpChips.displayName = 'FollowUpChips';
